import * as React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';

import { SessionContext } from '../AppProvider';
import { useLocaleText } from '../AppProvider/LocalizationProvider';

import { AccountLocaleContext } from './AccountLocaleContext';

export type AccountPreviewVariant = 'expanded' | 'condensed';

export interface AccountPreviewSlots {
  /**
   * The component used for the Avatar
   * @default Avatar
   */
  avatar?: React.ElementType;
  /**
   * The component used for the overflow icon button in the expanded variant
   * @default IconButton
   */
  moreIconButton?: React.ElementType;
  /**
   * The component used for the avatar icon button in the condensed variant
   * @default IconButton
   */
  avatarIconButton?: React.ElementType;
}

export interface AccountPreviewProps {
  /**
   * The prop used to customize the styling of the preview
   */
  sx?: SxProps;
  /**
   * The state of the Account popover
   * @default false
   */
  open?: boolean;
  /**
   * The components used for each slot inside.
   */
  slots?: AccountPreviewSlots;
  /**
   * The type of account details to display.
   * @property {'condensed'} condensed - Shows only the user's avatar.
   * @property {'expanded'} expanded - Displays the user's avatar, name, and email if available.
   * @default 'condensed'
   */
  variant?: AccountPreviewVariant;
  /**
   * The handler used when the preview is expanded
   */
  handleClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * The props used for each slot inside.
   */
  slotProps?: {
    avatar?: AvatarProps;
    moreIconButton?: IconButtonProps;
    avatarIconButton?: IconButtonProps;
  };
}

/**
 * The AccountPreview component displays user account information.
 *
 * Demos:
 *
 * - [Account](https://mui.com/toolpad/core/react-account/)
 *
 * API:
 *
 * - [AccountPreview API](https://mui.com/toolpad/core/api/account-preview)
 */
function AccountPreview(props: AccountPreviewProps) {
  const {
    sx,
    open,
    slots,
    slotProps,
    handleClick,
    variant = 'condensed',
  } = props;
  const session = React.useContext(SessionContext);
  const globalLocaleText = useLocaleText();
  const accountLocaleText = React.useContext(AccountLocaleContext);
  const localeText = { ...globalLocaleText, ...accountLocaleText };

  if (!session || !session.user) {
    return null;
  }

  const avatarContent = slots?.avatar ? (
    <slots.avatar />
  ) : (
    <Avatar
      src={session.user?.image || ''}
      alt={session.user?.name || session.user?.email || ''}
      sx={{
        width: variant === 'expanded' ? 48 : 32,
        height: variant === 'expanded' ? 48 : 32,
      }}
      {...slotProps?.avatar}
    />
  );

  if (variant === 'expanded') {
    return (
      <Stack
        direction="row"
        sx={{
          py: 1,
          px: 2,
          gap: 2,
          justifyContent: 'space-between',
          ...sx,
        }}
      >
        <Stack
          spacing={2}
          direction="row"
          sx={{
            overflow: 'hidden',
            justifyContent: 'flex-start',
          }}
        >
          {avatarContent}
          <Stack
            direction="column"
            sx={{
              overflow: 'hidden',
              justifyContent: 'space-evenly',
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{
                fontWeight: 'bolder',
              }}
            >
              {session.user?.name}
            </Typography>
            <Typography noWrap variant="caption">
              {session.user?.email}
            </Typography>
          </Stack>
        </Stack>
        {handleClick &&
          (slots?.moreIconButton ? (
            <slots.moreIconButton />
          ) : (
            <IconButton
              size="small"
              onClick={handleClick}
              {...slotProps?.moreIconButton}
              sx={{ alignSelf: 'center', ...slotProps?.moreIconButton?.sx }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          ))}
      </Stack>
    );
  }

  return (
    <Tooltip
      title={session.user.name ?? accountLocaleText?.accountPreviewTitle}
    >
      {slots?.avatarIconButton ? (
        <slots.avatarIconButton {...slotProps?.avatarIconButton} />
      ) : (
        <Stack sx={{ py: 0.5, ...sx }}>
          <IconButton
            size="small"
            aria-haspopup="true"
            onClick={handleClick}
            aria-expanded={open ? 'true' : undefined}
            aria-controls={open ? 'account-menu' : undefined}
            aria-label={localeText?.accountPreviewIconButtonLabel}
            {...slotProps?.avatarIconButton}
            sx={{
              margin: '0 auto',
              width: 'fit-content',
              ...slotProps?.avatarIconButton?.sx,
            }}
          >
            {avatarContent}
          </IconButton>
        </Stack>
      )}
    </Tooltip>
  );
}

AccountPreview.propTypes /* remove-proptypes */ = {
  /**
   * The state of the Account popover
   * @default false
   */
  open: PropTypes.bool,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * The handler used when the preview is expanded
   */
  handleClick: PropTypes.func,
  /**
   * The type of account details to display.
   * @property {'condensed'} condensed - Shows only the user's avatar.
   * @property {'expanded'} expanded - Displays the user's avatar, name, and email if available.
   * @default 'condensed'
   */
  variant: PropTypes.oneOf(['condensed', 'expanded']),
  /**
   * The props used for each slot inside.
   */
  slotProps: PropTypes.shape({
    avatar: PropTypes.object,
    moreIconButton: PropTypes.object,
    avatarIconButton: PropTypes.object,
  }),
  /**
   * The components used for each slot inside.
   */
  slots: PropTypes.shape({
    avatar: PropTypes.elementType,
    moreIconButton: PropTypes.elementType,
    avatarIconButton: PropTypes.elementType,
  }),
  /**
   * The prop used to customize the styling of the preview
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
} as any;

export { AccountPreview };
